import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useLocation } from 'react-router-dom';
import { adminAPI } from '../../services/api';
import { FiUsers, FiSettings, FiBarChart, FiDatabase, FiGrid, FiPlay, FiShield } from 'react-icons/fi';
import UserManagement from './UserManagement';
import KpiConfiguration from './KpiConfiguration';
import DataGridConfiguration from './DataGridConfiguration';
import EndpointManagement from './EndpointManagement';
import ActionConfiguration from './ActionConfiguration';
import SecurityDashboard from './SecurityDashboard';

const AdminPanel = () => {
  const location = useLocation();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const response = await adminAPI.getStats();
      setStats(response.data.stats);
    } catch (error) {
      console.error('Failed to load stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path);

  return (
    <div className="admin-panel">
      <div className="container">
        <div style={{ display: 'grid', gridTemplateColumns: '250px 1fr', gap: '30px' }}>
          {/* Sidebar */}
          <div className="admin-sidebar">
            <h2 style={{ marginBottom: '20px', color: '#333' }}>Admin Panel</h2>
            
            <nav>
              <ul style={{ listStyle: 'none', padding: 0 }}>
                <li style={{ marginBottom: '10px' }}>
                  <Link
                    to="/admin"
                    className={`nav-link ${isActive('/admin') && location.pathname === '/admin' ? 'active' : ''}`}
                    style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      padding: '10px',
                      borderRadius: '5px',
                      backgroundColor: isActive('/admin') && location.pathname === '/admin' ? '#e3f2fd' : 'transparent'
                    }}
                  >
                    <FiBarChart style={{ marginRight: '10px' }} />
                    Dashboard
                  </Link>
                </li>
                <li style={{ marginBottom: '10px' }}>
                  <Link
                    to="/admin/endpoints"
                    className={`nav-link ${isActive('/admin/endpoints') ? 'active' : ''}`}
                    style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      padding: '10px',
                      borderRadius: '5px',
                      backgroundColor: isActive('/admin/endpoints') ? '#e3f2fd' : 'transparent'
                    }}
                  >
                    <FiDatabase style={{ marginRight: '10px' }} />
                    API Endpoints
                  </Link>
                </li>
                <li style={{ marginBottom: '10px' }}>
                  <Link
                    to="/admin/kpis"
                    className={`nav-link ${isActive('/admin/kpis') ? 'active' : ''}`}
                    style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      padding: '10px',
                      borderRadius: '5px',
                      backgroundColor: isActive('/admin/kpis') ? '#e3f2fd' : 'transparent'
                    }}
                  >
                    <FiSettings style={{ marginRight: '10px' }} />
                    KPI Configs
                  </Link>
                </li>
                <li style={{ marginBottom: '10px' }}>
                  <Link
                    to="/admin/data-grids"
                    className={`nav-link ${isActive('/admin/data-grids') ? 'active' : ''}`}
                    style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      padding: '10px',
                      borderRadius: '5px',
                      backgroundColor: isActive('/admin/data-grids') ? '#e3f2fd' : 'transparent'
                    }}
                  >
                    <FiGrid style={{ marginRight: '10px' }} />
                    Data Grids
                  </Link>
                </li>
                <li style={{ marginBottom: '10px' }}>
                  <Link
                    to="/admin/actions"
                    className={`nav-link ${isActive('/admin/actions') ? 'active' : ''}`}
                    style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      padding: '10px',
                      borderRadius: '5px',
                      backgroundColor: isActive('/admin/actions') ? '#e3f2fd' : 'transparent'
                    }}
                  >
                    <FiPlay style={{ marginRight: '10px' }} />
                    Actions
                  </Link>
                </li>
                <li style={{ marginBottom: '10px' }}>
                  <Link
                    to="/admin/security"
                    className={`nav-link ${isActive('/admin/security') ? 'active' : ''}`}
                    style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      padding: '10px',
                      borderRadius: '5px',
                      backgroundColor: isActive('/admin/security') ? '#e3f2fd' : 'transparent'
                    }}
                  >
                    <FiShield style={{ marginRight: '10px' }} />
                    Security
                  </Link>
                </li>
                <li style={{ marginBottom: '10px' }}>
                  <Link
                    to="/admin/users"
                    className={`nav-link ${isActive('/admin/users') ? 'active' : ''}`}
                    style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      padding: '10px',
                      borderRadius: '5px',
                      backgroundColor: isActive('/admin/users') ? '#e3f2fd' : 'transparent'
                    }}
                  >
                    <FiUsers style={{ marginRight: '10px' }} />
                    Users
                  </Link>
                </li>
              </ul>
            </nav>
          </div>

          {/* Main Content */}
          <div className="admin-content">
            <Routes>
              <Route path="/" element={<AdminDashboard stats={stats} loading={loading} />} />
              <Route path="/endpoints" element={<EndpointManagement />} />
              <Route path="/kpis" element={<KpiConfiguration />} />
              <Route path="/data-grids" element={<DataGridConfiguration />} />
              <Route path="/actions" element={<ActionConfiguration />} />
              <Route path="/security" element={<SecurityDashboard />} />
              <Route path="/users" element={<UserManagement />} />
            </Routes>
          </div>
        </div>
      </div>
    </div>
  );
};

const AdminDashboard = ({ stats, loading }) => {
  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <p>Loading admin dashboard...</p>
      </div>
    );
  }

  return (
    <div>
      <h1>Admin Dashboard</h1>
      <p style={{ color: '#666', marginBottom: '30px' }}>
        Welcome to the admin panel. Here you can manage API endpoints, KPI configurations, and users.
      </p>

      {stats && (
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-value">{stats.totalUsers}</div>
            <div className="stat-label">Total Users</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{stats.activeUsers}</div>
            <div className="stat-label">Active Users</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{stats.totalEndpoints}</div>
            <div className="stat-label">API Endpoints</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{stats.totalKpiConfigs}</div>
            <div className="stat-label">KPI Configs</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{stats.recentUsers}</div>
            <div className="stat-label">New Users (7 days)</div>
          </div>
        </div>
      )}

      <div className="card">
        <div className="card-header">
          <h2 className="card-title">Quick Actions</h2>
        </div>
        
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px' }}>
          <Link to="/admin/endpoints" className="btn btn-primary" style={{ padding: '20px', textAlign: 'center' }}>
            <FiDatabase style={{ fontSize: '24px', marginBottom: '10px', display: 'block' }} />
            Manage API Endpoints
          </Link>
          
          <Link to="/admin/kpis" className="btn btn-primary" style={{ padding: '20px', textAlign: 'center' }}>
            <FiSettings style={{ fontSize: '24px', marginBottom: '10px', display: 'block' }} />
            Configure KPIs
          </Link>
          
          <Link to="/admin/data-grids" className="btn btn-primary" style={{ padding: '20px', textAlign: 'center' }}>
            <FiGrid style={{ fontSize: '24px', marginBottom: '10px', display: 'block' }} />
            Configure Data Grids
          </Link>
          
          <Link to="/admin/actions" className="btn btn-primary" style={{ padding: '20px', textAlign: 'center' }}>
            <FiPlay style={{ fontSize: '24px', marginBottom: '10px', display: 'block' }} />
            Configure Actions
          </Link>
          
          <Link to="/admin/users" className="btn btn-primary" style={{ padding: '20px', textAlign: 'center' }}>
            <FiUsers style={{ fontSize: '24px', marginBottom: '10px', display: 'block' }} />
            Manage Users
          </Link>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h2 className="card-title">Getting Started</h2>
        </div>
        
        <ol style={{ paddingLeft: '20px' }}>
          <li style={{ marginBottom: '10px' }}>
            <strong>Configure API Endpoints:</strong> Set up the external APIs that will provide data for KPIs
          </li>
          <li style={{ marginBottom: '10px' }}>
            <strong>Create KPI Configurations:</strong> Define how data from endpoints should be displayed
          </li>
          <li style={{ marginBottom: '10px' }}>
            <strong>Users Setup:</strong> Users can then add configured KPIs to their dashboards
          </li>
        </ol>
      </div>
    </div>
  );
};

export default AdminPanel;
